import { LoadingStateChart } from 'components/analytics';
import { ChartBase } from 'components/common';
import { ExportIcon } from 'components/icons';
import { CardBackground } from 'components/ui';
import { format } from 'date-fns';
import { lazy, Suspense, useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from 'store';
import type { PurchaseTiming } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';

const ExportMenuLazy = lazy(() => import('./PurchaseTimingExportMenu'));

interface PurchaseTimingOrders1And2Props {
  data: PurchaseTiming | undefined;
  loading: boolean;
}

export default function PurchaseTimingOrders1And2({
  data,
  loading,
}: PurchaseTimingOrders1And2Props) {
  const exportContainerRef = useRef<HTMLDivElement>(null);

  /** Start Cards Data */
  const second_growth = data?.second_growth
    ? Object.keys(data?.second_growth)[0]
    : '';
  const second_choke = data?.second_choke
    ? Object.keys(data?.second_choke)[0]
    : '';
  const second_growth_perc = data?.second_growth
    ? Number(Object.values(data?.second_growth)[0].toFixed(2))
    : 0;
  const second_choke_perc = data?.second_choke
    ? Object.values(data?.second_choke)[0]
    : 0;

  // Ends Cards Data

  /** Chart Data Starts */

  // Step - convert from days to weeks
  const step = 7;

  // Thresholds is used as reference to slice the dataset into categories and display at different colors

  const minThreshold = Math.ceil(parseInt(second_growth) / step) ?? 1;
  const maxThreshold = Math.ceil(parseInt(second_choke) / step) ?? 1;

  //  baseData converts the original data based on the steps (eg. from days to weeks)
  const baseData = Object.entries(data?.second_order_timing || {}).map(
    ([key, value]) => {
      const newKey = Math.ceil(parseInt(key) / step);
      return {
        [newKey]: value,
      };
    }
  );

  // Subsets of basedata based on the original and threshold settings
  const active = useMemo(
    () =>
      Object.assign(
        {},
        ...baseData.filter((item) => {
          const key = parseInt(Object.keys(item)[0]);
          return key <= minThreshold;
        })
      ),
    [baseData, minThreshold]
  );

  const atRisk = useMemo(
    () =>
      Object.assign(
        {},
        ...baseData.filter((item) => {
          const key = parseInt(Object.keys(item)[0]);
          return key > minThreshold && key < maxThreshold;
        })
      ),
    [baseData, minThreshold, maxThreshold]
  );

  const churn = useMemo(
    () =>
      Object.assign(
        {},
        ...baseData.filter((item) => {
          const key = parseInt(Object.keys(item)[0]);
          return key >= maxThreshold;
        })
      ),
    [baseData, maxThreshold]
  );

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: 'Active',
          backgroundColor: '#4F92F7',
          borderColor: '#4F92F7',
          data: active,
        },
        {
          label: 'At risk',
          backgroundColor: '#F87349',
          borderColor: '#F87349',
          data: atRisk,
        },
        {
          label: 'Churned',
          backgroundColor: '#D5CBB7',
          borderColor: '#D5CBB7',
          data: churn,
        },
      ],
    }),
    [active, atRisk, churn]
  );

  const options = {
    interaction: {
      mode: 'point',
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          text: 'Weeks from 1st order',
          display: true,
          font: { size: 15 },
        },
        stacked: true,
        ticks: {
          autoSkip: false,
        },
      },
      y: {
        ticks: {
          callback: (value: number) =>
            (value / 100).toLocaleString('en-US', {
              style: 'percent',
              minimumFractionDigits: value <= 1 ? 2 : 0,
              maximumFractionDigits: value <= 1 ? 2 : 0,
            }),
        },
        title: { text: '% purchased', display: true, font: { size: 15 } },
      },
    },
    plugins: {
      legend: {
        display: true,
        align: 'end',
        labels: {
          boxWidth: 20,
          font: {
            size: 15,
            lineHeight: '1.5px',
          },
        },
      },
      tooltip: {
        intersect: false,
        position: 'nearest',
        callbacks: {
          label: (tooltip: { parsed: { y: string } }) => tooltip.parsed.y + '%',
        },
      },
    },
  };

  const generateCSVData = useCallback(() => {
    const csvHeaders = [
      { label: 'Weeks', key: 'period' },
      { label: 'Active', key: 'active' },
      { label: 'At risk', key: 'at_risk' },
      { label: 'Churn', key: 'churn' },
    ];

    const activeCSV = Object.entries(active).map(([key, value]) => ({
      period: key,
      active: value as number,
    }));

    const atRiskCSV = Object.entries(atRisk).map(([key, value]) => ({
      period: key,
      at_risk: value as number,
    }));

    const churnCSV = Object.entries(churn).map(([key, value]) => ({
      period: key,
      churn: value as number,
    }));

    const csvData = [...activeCSV, ...atRiskCSV, ...churnCSV];

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }, [active, atRisk, churn]);

  const { currentStore } = useAppSelector(userDetailsState);
  const storeName = currentStore?.completeInfo?.store_name?.replaceAll(
    ' ',
    '_'
  );
  const filename = `${storeName}-purchase_timing-orders_1_and_2-${format(
    Date.now(),
    'yyyyMMdd'
  )}`;

  if (loading) return <LoadingStateChart chartTitle="Between orders 1 and 2" />;
  if (!data?.second_order_timing) return null;

  return (
    <div className="py-6" ref={exportContainerRef}>
      <div className="flex items-end justify-between px-2">
        <div className="whitespace-nowrap">
          <p className="text-2xl leading-7 whitespace-nowrap">
            Between 1st order and 2nd order
          </p>
          <p className="mt-2 text-base whitespace-nowrap">
            The graph below represents the distribution of when One timers
            convert to Repeats.
          </p>
        </div>

        <Suspense
          fallback={
            <div className="flex items-center px-5 py-2.5 space-x-2 rounded-lg bg-segments-gray focus:outline-none">
              <p>Export</p>
              <ExportIcon className="w-5 h-5" />
            </div>
          }
        >
          <ExportMenuLazy
            containerRef={exportContainerRef}
            generateCSVData={generateCSVData}
            filename={filename}
          />
        </Suspense>
      </div>

      <div className="mt-4">
        <CardBackground>
          <div className="p-10">
            <div className="flex justify-between">
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  Day {second_growth}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Most converted by
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  {second_growth_perc ?? '--'} %
                </p>
                <p className="text-base text-black text-opacity-50">
                  Day {second_growth} conversion
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  Day {second_choke}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Conversion slow after
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  {data.second_adp ? data.second_adp : '--'}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Avg. days
                </p>
              </div>
            </div>
            <div className="mt-8">
              <p className="text-base text-black text-opacity-50">
                <span className="font-bold">
                  {(second_growth_perc ?? '--') + '% '}
                </span>
                of repeat customers purchase their second order within
                <span className="font-bold"> {second_growth} </span>
                days after the first order. After that, an additional
                <span className="font-bold">
                  {' '}
                  {second_choke_perc - second_growth_perc > 0
                    ? (second_choke_perc - second_growth_perc).toFixed(2) + '%'
                    : '--'}{' '}
                </span>
                of orders happen between days
                <span className="font-bold"> {second_growth} </span> and
                <span className="font-bold"> {second_choke} </span> from the
                first order.
                {data.second_adp && (
                  <span>
                    The average days between orders 1 and 2 is
                    <span className="font-bold"> {data.second_adp} </span> days
                  </span>
                )}
              </p>
            </div>
            <hr className="my-8" />
            <ChartBase type="bar" data={chartData} options={options} />
          </div>
        </CardBackground>
      </div>
    </div>
  );
}
